import React, { Children, cloneElement, useEffect, useId, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '../../lib/utils';

/*
 * A highlight that glides between items (tabs, nav links, chips) using a shared layout
 * animation. Adapted from Motion Primitives "Animated Background" (MIT) via 21st.dev:
 * https://21st.dev/ibelick/animated-background. Children need a `data-id` prop.
 * Controlled when `value` is passed; otherwise click (or hover with enableHover) selects.
 */
const AnimatedBackground = ({
  children,
  value,
  defaultValue,
  onValueChange,
  className,
  transition = { type: 'spring', bounce: 0.15, duration: 0.5 },
  enableHover = false
}) => {
  const [internal, setInternal] = useState(defaultValue ?? null);
  const activeId = value !== undefined ? value : internal;
  const uniqueId = useId();

  useEffect(() => {
    if (defaultValue !== undefined) setInternal(defaultValue);
  }, [defaultValue]);

  const select = id => {
    if (value === undefined) setInternal(id);
    onValueChange?.(id);
  };

  return Children.map(children, (child, index) => {
    if (!child) return null;
    const id = child.props['data-id'];
    // Keep the child's own handlers (e.g. a menu that opens on hover)
    const interaction = enableHover
      ? {
          onMouseEnter: e => { child.props.onMouseEnter?.(e); select(id); },
          onMouseLeave: e => { child.props.onMouseLeave?.(e); select(defaultValue ?? null); }
        }
      : { onClick: e => { child.props.onClick?.(e); select(id); } };

    return cloneElement(
      child,
      {
        key: index,
        className: cn('relative inline-flex', child.props.className),
        'data-checked': activeId === id ? 'true' : 'false',
        ...interaction
      },
      <>
        <AnimatePresence initial={false}>
          {activeId === id && (
            <motion.span
              layoutId={`animated-background-${uniqueId}`}
              className={cn('absolute inset-0', className)}
              transition={transition}
              initial={{ opacity: defaultValue !== undefined ? 1 : 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          )}
        </AnimatePresence>
        <span className="relative z-10 inline-flex items-center gap-2">{child.props.children}</span>
      </>
    );
  });
};

export default AnimatedBackground;
